/**
 * Leading + trailing throttle for the table-change invalidations of
 * `createPowerSyncQuery`.
 *
 * The invalidations used to be throttled like this:
 *
 *   const request = () => {
 *     if (timeout) return;                        // drops every later call
 *     timeout = setTimeout(invalidate, ms);       // runs the FIRST one, late
 *   };
 *
 * That caps a burst (a sync applying thousands of rows) at one refetch per
 * window, but it also delays the common case, a single local write, by the
 * whole window: the user ticks a checkbox and the list shows the old row for
 * `ms` before it refetches.
 *
 * This runs the first request of a quiet period at once and opens a window.
 * Requests that arrive while the window is open are merged into ONE run when
 * it closes, which opens the next window. A burst therefore still refetches at
 * most once per window (plus the leading run), and a lone write refetches with
 * no added delay. A request inside a window is never dropped: the trailing run
 * picks up whatever changed after the leading one read the tables.
 */
export interface InvalidationThrottle {
  /** Runs now if no window is open; otherwise runs once when it closes.*/
  request: () => void;
  /** Drops any pending trailing run. Call it on cleanup.*/
  cancel: () => void;
}

export function createInvalidationThrottle(run: () => void, ms: number): InvalidationThrottle {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let pending = false;

  const openWindow = () => {
    timeout = setTimeout(() => {
      timeout = null;
      if (pending) {
        pending = false;
        run();
        openWindow();
      }
    }, ms);
  };

  return {
    request: () => {
      if (timeout) {
        pending = true;
        return;
      }
      run();
      openWindow();
    },
    cancel: () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      pending = false;
    },
  };
}
